import { Box, Button, Container, Typography } from '@mui/material'
import NextLink from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { Trans } from 'react-i18next'
import { useMediaQuery } from '~/hooks/useMediaQuery'

interface RequireSignInPageProps {
  /** ページタイプ(背景画像が変わる) */
  bgType: 'film' | 'door'
  /** タイトル */
  title?: string
  /** メッセージ */
  message?: string
  /** ボタン情報(text: ボタンテキスト, link:　ボタン押下時の遷移先, onClickButton: ボタン押下時のアクション) */
  buttonInfo?: {
    onClickButton: () => void
    text: string
  }
  /** ログアウトボタンを表示するかどうか */
  showLogout?: boolean
}

export function RequireSignInPage({
  bgType,
  title,
  message,
  buttonInfo,
  showLogout,
}: RequireSignInPageProps) {
  const { isSm } = useMediaQuery()
  const { push } = useRouter()

  const onClickButton = useCallback(() => {
    if (buttonInfo?.onClickButton !== undefined) {
      void buttonInfo.onClickButton()
    } else {
      void push('/login')
    }
  }, [buttonInfo, push])

  return (
    <Box
      sx={{
        backgroundImage: `url(/${
          bgType === 'film' ? 'film_bg.jpg' : 'door_bg.jpg'
        }), linear-gradient(rgba(0,0,0,0.7),rgba(0,0,0,0.7))`,
        backgroundBlendMode: 'overlay',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
      minHeight={{ xs: 'calc(100vh - 112px)', sm: 'calc(100vh - 164px)' }}
    >
      <Container maxWidth='sm' sx={{ position: 'relative' }}>
        <Box
          position='absolute'
          top={{ xs: '156px', sm: '180px' }}
          left={0}
          right={0}
          mx='auto'
          textAlign='center'
        >
          <Typography fontWeight={700} fontSize={{ xs: 20, sm: 28 }} mb={4}>
            {title}
          </Typography>
          <Typography fontSize={{ sm: 18 }} mb={2}>
            {message}
          </Typography>
          <Button
            sx={{ borderRadius: { xs: 0, sm: 18 }, width: 1, maxWidth: { xs: 300, sm: 200 } }}
            variant={isSm ? 'outlined' : 'contained'}
            onClick={onClickButton}
          >
            {buttonInfo?.text ?? <Trans i18nKey='login' />}
          </Button>
          {showLogout && (
            <Button variant='text' LinkComponent={NextLink} href='/logout'>
              <Trans i18nKey='logout' />
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  )
}
